import { Keyboard } from '@capacitor/keyboard';
import { StatusBarPlugin, StatusBar, Style as StatusBarStyle, StyleOptions, BackgroundColorOptions } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { PushNotifications } from '@capacitor/push-notifications';
import { BarcodeScannerPlugin } from '@paymash/capacitor-barcode-scanner-plugin';
import { Camera, CameraSource, CameraResultType, Photo } from '@capacitor/camera';
import { Device, DeviceInfo, DeviceId } from '@capacitor/device';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AppLauncher as CapacitorAppLauncher, CanOpenURLResult, OpenURLResult } from '@capacitor/app-launcher';
import { NativeSettings, IOSSettings } from 'capacitor-native-settings';
import { Intercom } from '@capacitor-community/intercom';
import { VirtualScrollerModule, VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
// myPOS Plugins
import { TransactionResult, ConnectOptions, PayOptions, IMyPosMiniPlugin } from '@paymash/capacitor-mypos-mini-types';
import { MyPosMiniPlugin } from '@paymash/capacitor-mypos-mini-plugin';


import {
  MakePaymentOptions,
  MakeRefundOptions,
  MyposGlassErrorCode,
  NfcState,
  PosInfo,
  RedirectToOptions,
  TransactionResponse,
  TransactionResponse as MyposGlassTransactionResponse,
} from '@paymash/capacitor-mypos-glass-plugin/dist/esm/models';
import { MyposGlassError } from '@paymash/capacitor-mypos-glass-plugin/dist/esm/error';
import { createMyposGlassPlugin } from '@paymash/capacitor-mypos-glass-plugin/dist/esm/factory';
import type { IMyposGlassPlugin } from '@paymash/capacitor-mypos-glass-plugin/dist/esm/plugin';
import { Capacitor } from '@capacitor/core';
// SIX
import {
  TimApiPlugin,
  TimApiTerminalStatus,
  TimApiError,
  TimApiErrorResultCode,
  ITimApiPaymentResult,
  TimApiTerminalConnectionParams,
  TimApiTerminalInterface,
} from '@paymash/capacitor-tim-api-plugin';
// SumUp
import { SumupPlugin } from '@paymash/capacitor-sumup-plugin';
// Customer display
import { createCustomerDisplayServerPlugin } from '@paymash/capacitor-customer-display-inapp-plugin';
// myPOS Plugins

interface IMyPosTransactionResult {
  dateTime: string;
  cardBrand: string;
  stan: number;
  tid: string | undefined;
}
interface IMyPosInfo {
  tid: string;
  currencyCode: string;
  currencyName: string;
  merchantData: any;
}
interface MyPosScannerResult {
  value: string;
}
enum MyPosScannerType {
  App = 'App',
  Broadcast = 'Broadcast',
}
type MyposN5PluginError = {};
const { MyPosN5Plugin } = {
  MyPosN5Plugin: {
    info(): Promise<any> {
      return Promise.resolve({});
    },
    pay(options: any): Promise<any> {
      return Promise.resolve({});
    },
    refund(options: any): Promise<any> {
      return Promise.resolve({});
    },
    print(receipt: object[]): Promise<void> {
      return Promise.resolve();
    },
    startScanner(scannerType: any): Observable<any> {
      return of({ value: '' });
    },
    openCashDrawer(): Promise<void> {
      return Promise.resolve();
    },
  },
};

const MYPOS_GLASS_UNAVAILABLE = 'myPOS Glass is not available on this platform';
const createUnavailableMyposGlassPlugin = (): IMyposGlassPlugin => ({
  info: () => Promise.reject(new Error(MYPOS_GLASS_UNAVAILABLE)),
  getNfcState: () => Promise.reject(new Error(MYPOS_GLASS_UNAVAILABLE)),
  makePayment: (options: MakePaymentOptions) => Promise.reject(new Error(MYPOS_GLASS_UNAVAILABLE)),
  makeRefund: (options: MakeRefundOptions) => Promise.reject(new Error(MYPOS_GLASS_UNAVAILABLE)),
  redirectTo: (options: RedirectToOptions) => Promise.resolve(false),
});

const MyposGlassPlugin: IMyposGlassPlugin =
  Capacitor.getPlatform() === 'android' ? createMyposGlassPlugin() : createUnavailableMyposGlassPlugin();

// Scanners
import {
  SocketScannerPlugin,
  ScannerAppInfo,
  ScannerResultAction,
  SocketScannerPluginError,
} from '@paymash/capacitor-socket-scanner-plugin';
// Crypto
import { createCipheriv, createDecipheriv, createHmac, randomBytes, Cipher, pbkdf2Sync } from 'crypto';

import { InAppBrowser, InAppBrowserObject, InAppBrowserOptions } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { Observable, of } from 'rxjs';

class OpenNativeSettings {
  open(option: any): Promise<{ status: boolean }> {
    return new Promise((resolve, reject) => resolve({ status: true }));
  }
}
// Sunmi
enum SunmiScannerType {
  /** Activity-intent scanner via com.sunmi.sunmiqrcodescanner — older Sunmi firmware only. */
  App = 'App',
  /** USB/external scanner via broadcast — requires supported USB device. */
  Broadcast = 'Broadcast',
  /** AIDL built-in scanner — recommended for D3 Mini and devices on newer firmware. */
  InnerScanner = 'InnerScanner',
  /** Auto-selects the best available method: InnerScanner → App → Broadcast. */
  Auto = 'Auto',
}
const { SunmiScannerPlugin, SunmiPrinterPlugin } = {
  SunmiScannerPlugin: {
    startScanner(scannerType: SunmiScannerType): any {},
  },
  SunmiPrinterPlugin: {
    bindService(): Promise<void> {
      return Promise.resolve();
    },
    unBindService(): Promise<void> {
      return Promise.resolve();
    },
    getPrinterModel() {
      return Promise.resolve({ value: 'D3 MINI 80' });
    },
    getPrinterPaperSize() {
      return Promise.resolve({ value: 0 });
    },
    printReceipt(receipt: any): Promise<void> {
      return Promise.resolve();
    },
    printLabel(receipt: any): Promise<void> {
      return Promise.resolve();
    },
  },
};

const AppLauncher = {
  canOpenUrl: (options: { url: string }): Promise<CanOpenURLResult> => CapacitorAppLauncher.canOpenUrl(options),
  openUrl: (options: { url: string }): Promise<OpenURLResult> => CapacitorAppLauncher.openUrl(options),
};

const requestCameraPermissions = () => {
  return Camera.requestPermissions({ permissions: ['camera'] }).then((status) => status.camera);
};

const requestGalleryPermissions = () => {
  return Camera.requestPermissions({ permissions: ['photos'] }).then((status) => status.photos);
};

export {
  Keyboard,
  StatusBar,
  StatusBarPlugin,
  StatusBarStyle,
  StyleOptions,
  BackgroundColorOptions,
  SplashScreen,
  PushNotifications,
  Camera,
  requestCameraPermissions,
  requestGalleryPermissions,
  CameraSource,
  CameraResultType,
  App,
  URLOpenListenerEvent,
  AppLauncher,
  CanOpenURLResult,
  OpenURLResult,
  TransactionResult,
  IMyPosTransactionResult,
  MyposGlassTransactionResponse,
  ITimApiPaymentResult,
  TimApiTerminalConnectionParams,
  TimApiTerminalInterface,
  MyPosScannerType,
  SunmiScannerType,
  SunmiScannerPlugin,
  createCustomerDisplayServerPlugin,
  MyposGlassErrorCode,
  NfcState,
  MakePaymentOptions,
  MakeRefundOptions,
  MyposGlassError,
  MyposGlassPlugin,
  PosInfo,
  TransactionResponse,
  MyPosN5Plugin,
  TimApiPlugin,
  SumupPlugin,
  SunmiPrinterPlugin,
  SocketScannerPlugin,
  VirtualScrollerModule,
  VirtualScrollerComponent,
  ScannerAppInfo,
  ScannerResultAction,
  SocketScannerPluginError,
  TimApiTerminalStatus,
  TimApiError,
  TimApiErrorResultCode,
  ConnectOptions,
  PayOptions,
  MyPosMiniPlugin,
  IMyPosMiniPlugin,
  IMyPosInfo,
  MyPosScannerResult,
  MyposN5PluginError,
  createCipheriv,
  createDecipheriv,
  createHmac,
  randomBytes,
  Cipher as Cipheriv,
  pbkdf2Sync,
  NativeSettings,
  IOSSettings,
  Intercom,
  InAppBrowser,
  InAppBrowserObject,
  InAppBrowserOptions,
  Photo,
  OpenNativeSettings,
  AndroidPermissions,
  BarcodeScannerPlugin,
  Device,
  DeviceInfo,
  DeviceId,
};
